// Webpack loaders
const configDefaults   = require('configDefaults');
const errorConfig      = require('errors');
const ThemeImageModel = require(`../../themes/${__themeCode}/images.js`);

// Native
import log from 'loglevel';
import * as SUGA from 'suga';

// Common Views
import * as VUE from 'vue'

// Models
import ImageModel from '../model/image-model';
import GameModel from '../model/game-model';

// Import tween library
import TweenMax from 'tween';
import CustomEase from '../vendor/custom-ease'

console.log(SUGA)

/**
 * Class for game.
 */
class Game
{
    /**
     * Constructs the object.
     */
    constructor()
    {
        window.__log = log;
        log.disableAll();

        // inject the common & default config options
        this.gameModel = new GameModel();
        this.gameModel = Object.assign(this.gameModel, configDefaults);

        window.__dpi = 2; // SUGA.Device.getDpi()
        window.__postfix = SUGA.Device.getResPostfix(window.__dpi);

        // start the progress bar
        SUGA.Progress.init('progress-container');
        SUGA.Progress.start();

        // extend PIXI
        VUE.Pixi.extend(__dpi)

        // choose the library
        const which = 'pariplay';

        // get the correct RGS integration
        switch (which)
        {
            case 'gluckgames':
                System.import(/* webpackChunkName: "gluckgames" */ 'gluckgames')
                    .then((module) => { this.loadGame(module.GluckGames); })
                    .catch((error) => { throw (new Error(error)); });
                break;

            case 'pariplay':
            default:
                System.import(/* webpackChunkName: "pariplay" */ 'pariplay')
                    .then((module) => { this.loadGame(module.Pariplay); })
                    .catch((error) => { throw (new Error(error)); });
        }
    }

    /**
     * Loads a game. Get data from the query string and make the loadGame call
     *
     * @param  {Function}  RGS  The rgs libr
     */
    loadGame(RGS)
    {
        // get the query string
        const queryData = UTIL.getQueryAsObj();

        // overwrite games which dont have a code
        if (__theme.apiGameCode)

        {
            queryData.gameCode = __theme.apiGameCode;
        }

        // create an instance of the RGS lib &
        // set it up with the URL querystring data
        // if you are managing your own history you need to handle `back`
        this.rgs = new RGS(queryData, __isMobile, () => this.forceBack());

        // toggle logging based on the this.gameModel.debug
        this.setupLogging();

        this.rgs.loadGame((err, rgsModel) =>
        {
            this.loadGameHandler(err, rgsModel);
        });
    }

    /**
     * Loads a game handler.
     *
     * @param  {object}  err       The error
     * @param  {object}  rgsModel  The rgs model
     */
    loadGameHandler(err, rgsModel)
    {
        // inject the data from the load game request
        this.gameModel = Object.assign(this.gameModel, rgsModel.getData());

        // inject the theme specific values
        this.gameModel = Object.assign(this.gameModel, __theme);

        // make the whole thing global, for easy logging
        window.__config = this.gameModel;

        // determine the state of the sound and mute, before making the UI
        this.gameModel.setSoundAndMute()

        // setup the currency manager
        this.setupCurrencyManager();

        // setup all the event listeners
        this.setupEventListeners();

        // toggle showing the debug info & options
        this.setupDebugging();

        // start Google Analytics tracking
        this.setupTrackingManager();

        // load fonts, images, etc
        this.setupIndependentManagers(() =>
        {
            // this essentially starts PIXI
            // and the render loop
            this.setupCanvasManager(() =>
            {
                // setup some views, so we can either
                // display the game or any errors
                this.setupInitialViews();

                // show the game and hide the loader
                this.showGameAfterDelay();

                // wait until we get a valid response
                // this callback will be called multiple times
                if (err)
                {
                    this.showNotificationView(err);

                    return;
                }

                // check we have the current & valid balance
                this.getInitialBalance(rgsModel, () =>
                {
                    // tell Google Analytics we are up and running
                    this.trackingManager.event('Request', 'LoadGame', (!!err));

                    // set the default reality check timer
                    this.setupRealityCheckManager();

                    // setup & start sound
                    this.setupSoundManager();

                    // start a replay game first if there is one to show
                    if (this.gameModel.replayOutcome)
                    {
                        this.showReplayMessage();

                        return;
                    }

                    // start a normal game (no replay)
                    this.startGame();
                });
            });
        });
    }

    /**
     * Gets the initial balance.
     *
     * @param  {Object}    rgsModel  The rgs model
     * @param  {Function}  cb        Completion handler
     */
    getInitialBalance(rgsModel, cb)
    {
        // now we validate the state of the game
        // we wait until now so everything is setup
        // and any actions such as errors or replays
        // can be displayed to the user
        this.rgs.getInitialBalance(rgsModel, (err, balance) =>
        {
            // wait until we get a valid response
            // this callback will be called multiple times
            if (err)
            {
                this.showNotificationView(err);

                return;
            }

            // if the balance is unknown. `-1` represents unknown
            if (balance === null || balance === undefined)
            {
                balance = -1;
            }

            // tell the rest of the app about the balance
            this.setBalance(balance, false);

            // update the balance if the process got a new balance
            if (balance !== null && balance !== undefined)
            {
                this.gameModel.setBalance(balance);
            }

            // tell Google Analytics we are up and running
            this.trackingManager.event('Request', 'LoadGame', (!!err));

            cb();
        });
    }

    /**
     * Adds the event listeners
     */
    setupEventListeners()
    {
        // notification view events
        SUGA.Event.on('start-replay', (data) => this.startReplay(data));
        SUGA.Event.on('end-replay', (data) => this.endReplay(data));
        SUGA.Event.on('reload', (e) => this.reload(e));
        SUGA.Event.on('hide-reality-check', (data) => this.hideRealityCheck(data));
        SUGA.Event.on('hide-error', (err) => this.hideNotificationView(err));
        SUGA.Event.on('home', (e) => this.rgs.goHome(e)); // RGS external link
        SUGA.Event.on('cashier', (e) => this.rgs.goCashier(e)); // RGS external link
        SUGA.Event.on('buy-more', (e) => this.rgs.goBuyMore(e)); // RGS external link
        SUGA.Event.on('history', (e) => this.rgs.goHistory(e)); // RGS external link
    }

    /**
     * To log or not to log, that is the question
     */
    setupLogging()
    {
        // turn loggin of by default
        if (this.gameModel.debug)
        {
            log.enableAll();
        }
    }

    /**
     * Set up the vies we will nees initially
     */
    setupInitialViews()
    {
        // get size of all the elements
        const size = this.canvasManager.gameDimensions;

        // localize introtext
        const maxPrize = this.currencyManager.localize(this.gameModel.maxPrize);
        const introtext = __t.str('introtext', { maxPrize });

        const menuOpts = {
            logoUrl: this.themeImageModel.logo,
            logoY: this.gameModel.logoYTitle,
            introText: introtext,
            introTextStyle: this.gameModel.styles.introText,
            showBuyMore: this.gameModel.showBuyMore,
            isOutofTickets: this.gameModel.isOutOfTickets(),
            buttonHandler: () => this.showGameView(),
        };

        // setup menu view
        const ThemeView = require(`../../themes/${__themeCode}/view/menu-view`).default;
        const themeView = new ThemeView();
        themeView.init(this.themeImageModel);

        this.menuView = new VUE.MenuView(size, menuOpts, this.gameModel, this.themeImageModel, themeView);
        this.showMenuView();

        const gameOpts = {
            font: this.gameModel.font,
            showTicketId: this.gameModel.showTicketId,
            placeBet: (data) => this.placeBet(data),
            endBet: (data) => this.endBet(data),
            resultShown: (data) => this.resultShown(data),
            stopAutoplay: (data) => this.stopAutoplay(true),
        };

        const GameView = require(`../../themes/${__themeCode}/view/game-view`).default;

        // setup game view aswell
        this.gameView = new GameView(size, gameOpts, this.themeImageModel);

        // setup this shit helpView
        if (this.gameModel.showHelpButton)
        {
            const helpOpts = {
                font: this.gameModel.font,
                helpUrl: this.gameModel.getHelpUrl(),
                balanceBarHeight: 45,
                onBackClick: () => this.back(),
            };

            this.helpView = new VUE.HelpView(size, helpOpts);
        }

        // make everything fit
        this.canvasManager.sizeToFit();
    }

    /**
     *  Setup the class that managers PIXI and all the view navigation
     *
     * @param  {Function}  cb  Handler when we are done
     */
    setupCanvasManager(cb)
    {
        // build the views needed for the canvas manager
        this.gameBg = new PIXI.Sprite.fromImage(this.themeImageModel.bg);

        const bottomBarViewOptions = {
            bgAlpha: 1,
            onHelpClick: (e) => this.showHelpView(e),
            onHomeClick: (e) => this.rgs.goHome(e),
            onMuteClick: (e) => this.toggleMute(e),
            onSettingsClick: (e) => this.toggleSettingsView(e),
        };

        this.bottomBarView = new VUE.BottomBarView(this.gameModel, bottomBarViewOptions, this.currencyManager);

        const options = {
            dpi: __dpi,
            font: this.gameModel.font,
            showFPS: this.gameModel.fps,
            forceCanvas: this.gameModel.canvas,
            backgroundAnchor: this.gameModel.backgroundAnchor,
            transitionSpeed: 300,
            parallaxBgOffeset: this.gameModel.parallax,
            defaultGameWidth: this.gameModel.gameWidth,
            defaultGameHeight: this.gameModel.gameHeight,
            onResize: (size) => this.onResize(size),
            onPageVisibility: (state) => this.onPageVisibility(state),
        };

        this.canvasManager = new VUE.Canvas(options, this.bottomBarView, this.gameBg);
        this.canvasManager.renderCanvas();
        this.canvasManager.sizeToFit();
        this.canvasManager.pushInitHistory('MenuView');

        this.addAdditionalViews();

        cb();
    }

    /**
     * Adds additional views.
     */
    addAdditionalViews()
    {
        const size = this.canvasManager.gameDimensions;

        if(!this.gameModel.isBasketMode() && this.gameModel.paytable)
        {
            const settingsOptions =
            {
                font: this.gameModel.font,
                primaryColor: this.gameModel.primaryColor,
                stake: this.gameModel.getStake(),
                stakes: this.gameModel.stakes,
                balance: this.gameModel.getBalance(),
                paytable: this.gameModel.paytable,
                autoplayRounds: this.gameModel.autoplayOptions,
                ukRegulations: this.gameModel.ukRegulations,
                onChange: (data) => this.settingsChanged(data),
                onSubmit: (data) => this.settingsSubmitted(data),
                onVisibilityChange: (data) => this.onSettingsViewVisibilityChange(data),
            }

            this.settingsView = new VUE.SettingsView(size, settingsOptions);

            this.canvasManager.addSettingsView(this.settingsView);
        }

        if (this.gameModel.hasBackgroundView)
        {
            const BackgroundView = require(`../../themes/${__themeCode}/view/background-view`).default;

            if (!BackgroundView.empty)
            {
                const backgroundView = new BackgroundView(size, this.themeImageModel);

                this.canvasManager.addBackgroundView(backgroundView);
            }
        }

        if (this.gameModel.hasForegroundView)
        {
            const ForegroundView = require(`../../themes/${__themeCode}/view/foreground-view`).default;

            if (!ForegroundView.empty)
            {
                const foregroundView = new ForegroundView(size, this.themeImageModel);

                this.canvasManager.addForegroundView(foregroundView);
            }
        }
    }

    /**
     * Setup the correct currency formatting
     */
    setupCurrencyManager()
    {
        const currCode = this.gameModel.currencyCode;
        const locale = this.gameModel.languageCode;

        this.currencyManager = new SUGA.Currency(SUGA.Data);
        this.currencyManager.setup(locale, currCode);

        window.__c = this.currencyManager;

        if (this.rgs.currencyLoaded)
        {
            this.rgs.currencyLoaded({
                cs: this.currencyManager.getCurrencySymbol(),
                cc: this.gameModel.currencyCode,
            });
        }
    }

    setupIndependentManagers(cb)
    {
        Promise.all([
            this.setupFontManager(),
            this.setupImageManager(),
            this.setupLocaleManager(),
        ]).then(cb).catch((reason) =>
        {

            cb()
        });
    }

    /**
     * Setup the image/asset loader
     */
    setupImageManager()
    {
        return new Promise((resolve, reject) =>
        {
            try
            {
                this.themeImageModel = new ThemeImageModel();

                const themeUrls = this.themeImageModel.preload();

                this.imageModel = new ImageModel(__dpi, __postfix);

                const urls = themeUrls.concat(this.imageModel.preload);

                SUGA.Loader.loadAssets(urls, resolve);
            }
            catch (e)
            {
                reject();
                 // eslint-disable-line
            }
        });
    }

    /**
     * Load the correct locale/language files
     */
    setupLocaleManager()
    {
        return new Promise((resolve) =>
        {
            const lang = this.gameModel.languageCode;

            const hackFunctionToLoadLangFiles = () => // eslint-disable-line
            {
                const path = require(`lang/${lang}.json`);  // eslint-disable-line
            }

            this.localeManager = new SUGA.Locale(this.gameModel.defaultLanguage, './assets/lang/', __version);

            this.localeManager.setLocale(lang, resolve);

            window.__t = this.localeManager;
        });
    }

    /**
     * Load the fonts for the theme
     */
    setupFontManager()
    {
        return new Promise((resolve) =>
        {
            // copy all the fonts over
            function fake(val)  // eslint-disable-line
            {
                require(`../../themes/${__themeCode}/font/${val}.eot`);
                require(`../../themes/${__themeCode}/font/${val}.svg`);
                require(`../../themes/${__themeCode}/font/${val}.ttf`);
                require(`../../themes/${__themeCode}/font/${val}.woff`);
                require(`../../themes/${__themeCode}/font/${val}.woff2`);
            }

            // the css file loads those fonts
            const fontCssUrl = require(`../../themes/${__themeCode}/fonts.css`);
            const fontsToLoad = [this.gameModel.getFontWithVariations(), this.gameModel.secondaryFont];

            SUGA.Font.loadFonts(fontsToLoad, fontCssUrl, __version, (styles) =>
            {
                styles.forEach((style) => new PIXI.Text('test', style));

                resolve();
            });
        });
    }

    /**
     * Start up the audio
     */
    setupSoundManager()
    {
        if (__isMobile)
        {
            this.mute()
        }

        return new Promise((resolve, reject) =>
        {
            // get url of JSON sprite sheet
            const audioSpriteUrl = require(`../../themes/${__themeCode}/sound/audio-sprite.json`);

            // fetch the JSON sprite sheet
            SUGA.Network.getJSON(audioSpriteUrl, __version, (err, audioSpriteObj) =>
            {
                // catch error
                if (err || !audioSpriteObj)
                {
                    reject();

                    return;
                }

                resolve();

                // make background audio loop
                audioSpriteObj.sprite.music.push(true);

                // get audio urls
                const mp3 = require(`../../themes/${__themeCode}/sound/audio-sprite.mp3`);
                const ogg = require(`../../themes/${__themeCode}/sound/audio-sprite.ogg`);

                // build options
                const options = {
                    mp3: `${mp3}?v=${__version}`, // version audio files
                    ogg: `${ogg}?v=${__version}`, // version audio files
                    masterVolume: this.gameModel.getSound(),
                    volumes: this.gameModel.volumes,
                    mute: this.gameModel.getMute(),
                    audioSpriteObj,
                    startOnInteraction: __isMobile, // whether to start immediately or after user interaction
                    element: document.getElementById('canvas')
                };

                const onStart = () =>
                {
                    console.log('started')
                    // start background audio
                    SUGA.Sound.play('music');
                }

                // Load the audio sprite json and setup for playback
                SUGA.Sound.init(options, onStart);
            });
        });
    }

    /**
     * Start Google Analytics event and error tracking
     */
    setupTrackingManager()
    {
        this.trackingManager = new SUGA.Tracking(this.gameModel.gameCode, __version);
    }

    /**
     * Show the debug options
     */
    setupDebugging()
    {
        const prizeCount = (this.gameModel.paytable) ? this.gameModel.paytable.length : 0;

        if (this.gameModel.debug)
        {
            System.import(/* webpackChunkName: "debug" */ 'debugManager')
                .then((module) =>
                {
                    const debugManager = module.default;

                    debugManager.render(prizeCount);
                })
                .catch((error) => { throw (new Error(error)); });
        }
    }

    /**
     * Set up reality check stuff
     */
    setupRealityCheckManager()
    {
        if (this.gameModel.showRealityCheck)
        {
            this.trackingManager.event('RealityCheck', 'event', 'show');

            const interval = this.gameModel.realityCheckSeconds;

            const handler = ((msg) =>
            {
                this.showRealityCheck(msg);
            });

            // initialize with reality check
            SUGA.Reality.init(interval, handler, false);

            // start the linterval listener
            SUGA.Reality.start();
        }
    }

    /**
     * Hide the load screen and display the game after short delay
     */
    showGameAfterDelay()
    {
        setTimeout(() =>
        {
            SUGA.Progress.done();

            const ele = document.getElementById('initial-loader');

            ele.style.opacity = 0;
            ele.style.display = 'none';

            this.canvasManager.showCanvasEle();
        }, this.gameModel.hideLoaderDelay);
    }

    /**
     * Starts a game, after any loading messages or replays
     */
    startGame()
    {
        this.gameView.reset();

        // start normal game
        this.enableUserActions();

        this.canvasManager.popOverlay();

        this.rgs.balanceHandler = (balance) =>
        {
            if (window.UTIL.isNotNumeric(balance)) return
            this.setBalance(balance);
        }

        this.setBalance(this.gameModel.getBalance());

        if (this.gameModel.isOutOfTickets() && this.gameModel.showBuyMoreButton)
        {
            this.showNotificationView({ code: 'NO_TICKETS_LEFT' });
        }
    }

    /**
     * Places a bet
     */
    placeBet(delay)
    {

        if (this.gameModel.showRealityCheck)
        {
            SUGA.Reality.canShowRealityCheck(false);
        }

        if (this.gameModel.isOutOfTickets())
        {
            this.noTicketsLeft();

            if (this.gameModel.showBuyMoreButton)
            {
                this.showNotificationView({ code: 'NO_TICKETS_LEFT' });

                return;
            }
        }

        // if we are using a delayed play e.g. autoplay
        if (delay)
        {
            PIXI.setTimeout(delay, () =>
            {
                // if the user cancelled autoplay in the meantime
                if (this.gameModel.isAutoplaying())
                {
                    this.placeBet();
                }
                else
                {
                    this.gameView.enableInteraction()
                    this.enableUserActions();
                }

            });
            return;
        }

        this.disableNavigationButtons()

        this.bottomBarView.autoplayClearReason();

        this.gameModel.hasUserStartedAutoplay = true

        const stake = this.gameModel.getStake();

        this.rgs.placeBet(stake, (err, betModel, code) =>
        {
            if (err)
            {
                this.showNotificationView(err);

                this.gameView.enableInteraction()

                return;
            }

            betModel.isAutoplay = this.gameModel.isAutoplaying();



            if (betModel.isAutoplay)
            {
                const roundsLeft = this.gameModel.decreaseAutoplay()

                this.bottomBarView.updateAutoplay(roundsLeft,this.gameModel.autoplayRounds)
            }

            this.setBalance(betModel.balance.balanceBefore);

            this.setRemainingTickets(betModel.balance.roundsLeftAfter)

            this.canvasManager.popAllOverlays();

            this.gameView.executeBet(betModel, err, code);

            this.canvasManager.disableScrolling();
        });
    }

    /**
     * This happens as soon as the result is seen
     * aka before any winning animations
     *
     * @param  {<type>}  betModel  The bet model
     */
    resultShown(betModel)
    {
        this.rgs.endBet(betModel.getEndBetData());
        if (this.gameModel.isAutoplaying() || betModel.isReplay )
        {
            return
        }
        this.enableNavigationButtons()

    }

    /**
     * Ends a round. After any winning animations
     *
     * @param  {object}  data  The data
     */
    endBet(betModel)
    {
        this.setBalance(betModel.balance.balanceAfter);

        if (betModel.isReplay)
        {
            this.showEndReplayMessage();
            return;
        }

        if (this.gameModel.showRealityCheck)
        {
            const shouldStop = SUGA.Reality.canShowRealityCheck(true)

            if (shouldStop)
            {
                // turn off autoplay here

                return
            }
        }


        if (this.gameModel.isAutoplaying())
        {
            const ticket = betModel.ticket
            const stopReason = this.gameModel.updateAutoplayState(ticket.totalWinAmount,ticket.totalBet)

            if (stopReason !== null)
            {
                this.bottomBarView.autoplayStopped(stopReason);
                this.enableNavigationButtons()
            }

            if (this.gameModel.autoplayedRounds > 0 && this.gameModel.hasUserStartedAutoplay)
            {
                this.placeBet(2);
            }
            else
            {
                this.gameModel.resetAutoplay();
                this.gameView.resetAutoplay()
                this.bottomBarView.updateAutoplay(this.gameModel.autoplayedRounds,this.gameModel.autoplayRounds);
                this.enableNavigationButtons()
            }
        }
    }

    /**
     * Shows the replay message.
     */
    showReplayMessage()
    {
        this.showNotificationView({ code: 'REPLAY_START' });
    }

    /**
     * Starts a replay.
     */
    startReplay()
    {
        const ticketModel = this.gameModel.replayOutcome;

        this.rgs.replayBet(ticketModel, (err, betModel) =>
        {
            if (err)
            {
                this.showNotificationView(err);

                return;
            }

            this.trackingManager.event('Replay', 'event', 'start');

            this.setBalance(betModel.getBalanceBefore());
            this.showGameView(true);
            this.disableUserActions();

            PIXI.setTimeout(2, () =>
            {
                this.gameView.executeBet(betModel);
            });
        });
    }

    stopAutoplay(resetSettings)
    {
        if (resetSettings && this.settingsView)
        {
            this.settingsView.resetModel()
        }

        this.gameModel.stopAutoplay()
        this.bottomBarView.updateAutoplay(this.gameModel.autoplayedRounds,this.gameModel.autoplayRounds);

        this.gameView.enableInteraction()
        this.enableUserActions();
    }

    /**
     * Ends a replay.
     */
    showEndReplayMessage()
    {
        PIXI.setTimeout(2, () =>
        {
            this.showNotificationView({ code: 'REPLAY_DONE' });
        });
    }

    endReplay()
    {
        this.setBalance(this.gameModel.getBalance(), false);
        this.startGame();
        this.showMenuView();
    }

    /**
     * Shows the game view.
     *
     * @param  {boolean}  isReplay  Indicates if replay
     */
    showGameView(isReplay)
    {
        this.trackingManager.event('GameView', 'views', 'show');

        const supressEvents = isReplay;

        this.canvasManager.popAllOverlays(supressEvents);
        this.canvasManager.popAllViews();
        this.canvasManager.pushView(this.gameView);
        this.gameView.enableInteraction();

        const duration = 2000;

        SUGA.Sound.fade('music', 0.2, duration);
    }

    /**
     * Shows the menu view.
     */
    showMenuView()
    {
        this.trackingManager.event('MenuView', 'views', 'show');

        this.canvasManager.popOverlay();
        this.canvasManager.popAllViews();
        this.canvasManager.pushView(this.menuView);
    }

    /**
     * Shows the help view.
     */
    showHelpView()
    {
        if (this.gameModel.getHelpUrl() === null)
        {
            return;
        }

        if (this.canvasManager.isTransitioning)

        {
            return;
        }

        if (this.helpView.isShowing)
        {
            this.back();

            return;
        }

        if (this.settingsView && this.settingsView.isOpen)
        {
            this.settingsView.close()
            return
        }

        this.trackingManager.event('HelpView', 'views', 'show');

        this.canvasManager.pushView(this.helpView);
    }

    /**
     * Shows the error view
     *
     * @param  {object}  rawErr  The raw error
     */
    showNotificationView(rawErr)
    {
        let code;
        let serverMsg;

        if (rawErr.code)
        {
            code = rawErr.code;
        }

        if (rawErr.msg)
        {
            serverMsg = rawErr.msg;
        }

        const err = UTIL.clone(errorConfig[code]);

        if (!err)
        {
            return;
        }

        if (err.showServerMsg && serverMsg)
        {
            err.msg = serverMsg;
        }

        if (err.buttons)
        {
            for (const button of Array.from(err.buttons))
            {
                button.text = __t.str(button.textKey);
            }
        }

        else
        {
            err.buttons = [];
        }

        if (err.fatal)
        {
            this.stopAutoplay(true);
            this.disableUserActions();
            this.mute();
            this.gameView.reset();
        }

        this.trackingManager.exception(`${code}:${serverMsg}`, err.fatal);

        const size = this.canvasManager.gameDimensions;

        if (!this.notificationView)
        {
            const options = {
                font: this.gameModel.font,
                primaryColor: this.gameModel.primaryColor,
                showCashierButton: this.gameModel.showCashierButton,
                showHistoryButton: this.gameModel.showHistoryButton,
                showHomeButton: this.gameModel.showHomeButton,
                showBuyMoreButton: this.gameModel.showBuyMoreButton,
                buttonImages: {
                    // bgUrl: this.imageModel.buttonBgImage,
                    normalUrl: this.imageModel.buttonSecondaryNormalImage,
                    hoverUrl: this.imageModel.buttonSecondaryHoverImage,
                },
            };

            this.notificationView = new VUE.NotificationView(size, options);
        }

        this.notificationView.setup(err.title, err.msg, err.buttons, size, () =>
            {

            });

        this.canvasManager.pushOverlay(this.notificationView);
    }

    /**
     * Hides the error view
     */
    hideNotificationView()
    {
        // this.gameView.resetAfterNotification();

        this.canvasManager.popOverlay();
    }

    toggleSettingsView(e)
    {
        if (this.canvasManager.isViewShowing('HelpView'))
        {
            this.canvasManager.popView()
            return
        }

        if(this.settingsView && !this.settingsView.isOpen)
        {
            this.stopAutoplay(false)
        }

        this.settingsView.toggle()
    }

    onSettingsViewVisibilityChange(isOpen)
    {
        this.bottomBarView.toggleSettings(isOpen)
    }

    /**
     * Sets the balance.
     *
     * @param  {Number}   balance  The balance
     * @param  {boolean}  animate  The animate
     */
    setBalance(balance, animate = true)
    {
        this.bottomBarView.updateBalance({ balance, animate });

        if (balance !== this.gameModel.getBalance())
        {
            this.rgs.balanceChanged(balance);
        }
    }

    setRemainingTickets(tickets)
    {
        this.gameModel.setRoundsLeft(tickets)
        this.bottomBarView.updateTicketCount(tickets)
    }

    /**
     * Shows the reality check.
     *
     * @param  {string}  msg  The message to dispaly
     */
    showRealityCheck(time)
    {
        this.disableUserActions();

        const xHours = __t.str('%{smart_count} hours', { smart_count: time.hours });
        const xMins = __t.str('%{smart_count} mins', { smart_count: time.minutes });
        const msg = __t.str('You have been playing for %{xHours} and %{xMins}', { xHours, xMins });

        if (this.isShowingRealityCheck)
        {
            this.notificationView.updateMsg(msg);

            return;
        }

        this.showNotificationView({ code: 'REALITY_CHECK', msg });
        this.isShowingRealityCheck = true;

        this.trackingManager.event('RealityView', 'views', 'show');
    }

    /**
     * Hides the reality check.
     */
    hideRealityCheck()
    {
        this.isShowingRealityCheck = false;
        this.canvasManager.popOverlay();
        this.enableUserActions();

        // this.gameView.resetAfterNotification();
    }

    /**
     * Called on resize
     */
    onResize(size)
    {
        this.rgs.resize(size.viewportWidth, size.viewportHeight);
    }

    /**
     * Reload the game
     */
    reload()
    {
        location.reload();
    }

    /**
     * When page becomes visible or not
     *
     * @param  {boolean}  isVisible  Indicates if visible
     */
    onPageVisibility(isVisible)
    {
        if (isVisible)
        {
            SUGA.Sound.resumeAll();
        }
        else
        {
            SUGA.Sound.pauseAll();
        }
    }

    /**
     * Toggle the sound
     */
    toggleMute()
    {
        if (!this.gameModel.getMute())
        {
            this.mute();
        }
        else
        {
            this.unMute();
        }
    }

    /**
     * Method to mute game
     */
    mute()
    {
        this.gameModel.setMute(true);
        SUGA.Sound.setMute(true);
        this.bottomBarView.displaySoundOffButton();

        this.trackingManager.event('Mute', 'button', true);
    }

    /**
     * Method to unmute the games
     */
    unMute()
    {
        this.gameModel.setMute(false);
        SUGA.Sound.setMute(false);
        this.bottomBarView.displaySoundOnButton();

        this.trackingManager.event('Mute', 'button', false);
    }

    settingsChanged(data)
    {
        this.gameModel.stake = data.stake

        this.bottomBarView.updateStake(this.gameModel.getStake())
    }

    settingsSubmitted(data)
    {
        Object.assign(this.gameModel,data)

        this.gameModel.resetAutoplay()

        this.bottomBarView.updateStake(this.gameModel.getStake())
        this.bottomBarView.updateAutoplay(this.gameModel.autoplayedRounds,this.gameModel.autoplayRounds);


        if (this.helpView)
        {
            const h = (this.gameModel.autoplayRounds > 0) ? 45 + 21 : 45;
            this.helpView.setBalanceBarHeight(h)
        }
    }

    /**
     * Called when we are out of tickets
     */
    noTicketsLeft()
    {
        this.rgs.noTicketsLeft();
    }

    /**
     * Called when game will reveal all
     */
    willRevealAll()
    {
        this.rgs.willRevealAll();
    }

    /**
     * Enables the user actions.
     */
    enableUserActions()
    {
        this.canvasManager.enableUserActions();
    }

    /**
     * Disables the user actions.
     */
    disableUserActions()
    {
        this.canvasManager.disableUserActions();
    }

    /**
     * Enables the navigation buttons.
     */
    enableNavigationButtons()
    {
        this.bottomBarView.enableNavigationButtons();
    }

    /**
     * Disables the navigation buttons.
     */
    disableNavigationButtons()
    {
        this.bottomBarView.disableNavigationButtons();
    }

    /**
     * Go back - we are mananging our own history
     */
    back()
    {
        this.canvasManager.back();
    }

    /**
     * Force the game to the previous page
     * Needed when we manage our own history
     */
    forceBack()
    {
        this.canvasManager.forceBack();
    }

    /**
     * Tracks events to Google Analy
     *
     * @param  {array}  data  The data
     */
    trackEvent(data)
    {
        this.trackingManager.event(data[0], data[1], data[2]);
    }
}

new Game(); // eslint-disable-line




// WEBPACK FOOTER //
// ./babel/app/game.js